import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'

import { DailyBriefingPopup } from '../components/DailyBriefing'
import type { getBriefing } from '../services/briefingService'

/**
 * The Daily Briefing popup's home (MYD-645): above the routes, beside Chat and
 * Ask MDT, so following a link in the briefing does not close it.
 *
 * It used to live inside the Home screen. A briefing row links to another
 * page, and leaving Home unmounted the popup with it. Here it outlives the
 * page change, stays where the user dragged it, and keeps the briefing it
 * already loaded.
 *
 * Mounted by AskMdtRoot, so it exists only where Ask MDT is offered; where it
 * is not, `useDailyBriefing()` is null and Home shows no label.
 *
 * Closed when the app loads: opening it on every load was tried and taken
 * back (MYD-644).
 */

export interface DailyBriefingControls {
	open: boolean
	toggle: () => void
	close: () => void
	/** The header label, so the popup can open just under it. */
	triggerRef: React.RefObject<HTMLButtonElement>
}

const DailyBriefingContext = createContext<DailyBriefingControls | null>(null)

/** The popup's controls, or null where the Daily Briefing is not offered. */
export const useDailyBriefing = () => useContext(DailyBriefingContext)

export function DailyBriefingProvider({ children, load }: { children: React.ReactNode; load?: typeof getBriefing }) {
	const [open, setOpen] = useState(false)
	const triggerRef = useRef<HTMLButtonElement>(null)
	const toggle = useCallback(() => setOpen(current => !current), [])
	const close = useCallback(() => setOpen(false), [])

	// Read once per open (FloatingPanel): just under the label, when there is
	// one on screen.
	const anchor = useCallback(() => {
		const rect = triggerRef.current?.getBoundingClientRect()
		return rect ? { x: rect.left, y: rect.bottom + 8 } : null
	}, [])

	const value = useMemo(() => ({ open, toggle, close, triggerRef }), [open, toggle, close])

	return (
		<DailyBriefingContext.Provider value={value}>
			{children}
			<DailyBriefingPopup open={open} onClose={close} anchor={anchor} load={load} />
		</DailyBriefingContext.Provider>
	)
}

export default DailyBriefingProvider
